// src/components/admin/AdminFeedbackPage.jsx
// Redesigned — card layout, stat summary, mobile-first

import { useState, useEffect, useCallback } from 'react';
import { api } from '../../utils/api.js';
import { toastSuccess, toastInfo } from '../../utils/toast.js';

/* ── Constants ───────────────────────────────────────────────── */
const CATEGORIES = ['all', 'feature', 'bug', 'ux', 'other'];
const STATUSES   = ['all', 'new', 'reviewed', 'done', 'dismissed'];

const CAT_META = {
  feature: { icon: '💡', label: 'Feature',  color: '#C9A84C' },
  bug:     { icon: '🐛', label: 'Bug',      color: '#E57373' },
  ux:      { icon: '🎨', label: 'UI / UX',  color: '#9575CD' },
  other:   { icon: '💬', label: 'Other',    color: '#4DB6AC' },
};

const STATUS_META = {
  new:       { color: '#C9A84C', bg: 'rgba(201,168,76,.13)',  border: 'rgba(201,168,76,.35)',   dot: '#C9A84C'  },
  reviewed:  { color: '#6495ED', bg: 'rgba(100,149,237,.12)', border: 'rgba(100,149,237,.3)',   dot: '#6495ED'  },
  done:      { color: '#2ECC8A', bg: 'rgba(46,204,138,.1)',   border: 'rgba(46,204,138,.28)',   dot: '#2ECC8A'  },
  dismissed: { color: 'rgba(240,234,214,.3)', bg: 'rgba(255,255,255,.04)', border: 'rgba(255,255,255,.1)', dot: 'rgba(240,234,214,.25)' },
};

const CSS = `
  .fb-page { padding: 24px 20px; max-width: 960px; font-family: 'Inter', sans-serif; }
  .fb-stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 24px; }
  .fb-stat-card { background: rgba(255,255,255,.03); border: 1px solid rgba(255,255,255,.07); padding: 14px 16px; border-radius: 8px; }
  .fb-stat-card:first-child { border-color: rgba(201,168,76,.25); background: rgba(201,168,76,.06); }
  .fb-controls { display: flex; gap: 10px; margin-bottom: 16px; align-items: center; flex-wrap: wrap; }
  .fb-search { flex: 1; min-width: 180px; }
  .fb-chips { display: flex; gap: 6px; flex-wrap: wrap; }
  .fb-chip { padding: 5px 13px; font-size: 11px; font-weight: 600; cursor: pointer; border-radius: 20px; border: 1px solid rgba(255,255,255,.08); background: rgba(255,255,255,.03); color: rgba(240,234,214,.4); transition: all .15s; white-space: nowrap; letter-spacing: .02em; }
  .fb-chip.active { background: rgba(201,168,76,.15); border-color: rgba(201,168,76,.4); color: #C9A84C; }
  .fb-chip:hover:not(.active) { background: rgba(255,255,255,.06); color: rgba(240,234,214,.7); }
  .fb-card { background: rgba(255,255,255,.025); border: 1px solid rgba(255,255,255,.07); border-radius: 10px; margin-bottom: 8px; overflow: hidden; transition: border-color .2s, box-shadow .2s; }
  .fb-card:hover { border-color: rgba(255,255,255,.12); }
  .fb-card.open { border-color: rgba(201,168,76,.22); box-shadow: 0 4px 24px rgba(0,0,0,.3); }
  .fb-card-row { display: flex; align-items: center; gap: 12px; padding: 14px 16px; cursor: pointer; }
  .fb-cat-badge { width: 36px; height: 36px; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 16px; flex-shrink: 0; }
  .fb-card-body { flex: 1; min-width: 0; }
  .fb-card-msg { font-size: 13px; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-bottom: 4px; }
  .fb-card-meta { font-size: 11px; color: rgba(240,234,214,.35); display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
  .fb-card-right { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
  .fb-status-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
  .fb-status-pill { padding: 3px 10px; font-size: 10px; font-weight: 700; letter-spacing: .05em; text-transform: uppercase; border-radius: 20px; white-space: nowrap; }
  .fb-chevron { font-size: 10px; color: rgba(240,234,214,.2); transition: transform .2s; }
  .fb-chevron.open { transform: rotate(180deg); }
  .fb-detail { padding: 0 16px 18px; border-top: 1px solid rgba(255,255,255,.05); }
  .fb-detail-msg { margin: 14px 0; padding: 14px 16px; background: rgba(255,255,255,.03); border-left: 3px solid rgba(201,168,76,.3); border-radius: 0 6px 6px 0; font-size: 13px; color: rgba(240,234,214,.85); line-height: 1.8; }
  .fb-section-label { font-size: 9px; letter-spacing: .1em; text-transform: uppercase; color: rgba(240,234,214,.22); margin-bottom: 8px; }
  .fb-status-btns { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 16px; }
  .fb-status-btn { padding: 6px 14px; font-size: 11px; font-weight: 600; letter-spacing: .04em; text-transform: uppercase; cursor: pointer; border-radius: 6px; transition: all .15s; }
  .fb-note-input { width: 100%; box-sizing: border-box; resize: vertical; background: rgba(255,255,255,.04); border: 1px solid rgba(255,255,255,.1); color: var(--cream,#F0EAD6); font-size: 12px; line-height: 1.7; padding: 10px 12px; outline: none; font-family: 'Inter', sans-serif; border-radius: 6px; transition: border-color .15s; }
  .fb-note-input:focus { border-color: rgba(201,168,76,.4); }
  .fb-save-btn { margin-top: 8px; padding: 8px 20px; font-size: 11px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; cursor: pointer; background: rgba(201,168,76,.1); border: 1px solid rgba(201,168,76,.3); color: var(--gold,#C9A84C); border-radius: 6px; transition: background .15s; }
  .fb-save-btn:hover { background: rgba(201,168,76,.2); }
  .fb-empty { padding: 60px 20px; text-align: center; color: rgba(240,234,214,.25); font-size: 13px; }
  .fb-sort-select { background: rgba(255,255,255,.04); border: 1px solid rgba(255,255,255,.1); color: rgba(240,234,214,.6); font-size: 11px; padding: 7px 12px; cursor: pointer; border-radius: 6px; outline: none; font-family: 'Inter', sans-serif; }

  @media (max-width: 768px) {
    .fb-page { padding: 16px 14px; }
    .fb-stats { grid-template-columns: repeat(2, 1fr); gap: 8px; }
    .fb-controls { flex-direction: column; align-items: stretch; }
    .fb-search { min-width: unset; }
    .fb-chips { overflow-x: auto; flex-wrap: nowrap; scrollbar-width: none; -webkit-overflow-scrolling: touch; padding-bottom: 2px; }
    .fb-chips::-webkit-scrollbar { display: none; }
    .fb-card-meta { gap: 5px; }
    .fb-card-right .fb-status-pill { display: none; }
  }
  @media (max-width: 480px) {
    .fb-stats { grid-template-columns: repeat(2, 1fr); }
    .fb-card-row { padding: 12px; gap: 10px; }
  }
`;

/* ── Helpers ─────────────────────────────────────────────────── */
const fmtDate = d => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
const fmtDateFull = d => new Date(d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });

/* ── Stat card ───────────────────────────────────────────────── */
function StatCard({ label, value, sub, accent }) {
  return (
    <div className="fb-stat-card">
      <div style={{ fontSize: 22, fontWeight: 800, color: accent || 'var(--cream,#F0EAD6)', letterSpacing: '-.01em', fontFamily: "'Bebas Neue',sans-serif" }}>{value}</div>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'rgba(240,234,214,.55)', marginTop: 1, letterSpacing: '.03em' }}>{label}</div>
      {sub && <div style={{ fontSize: 10, color: 'rgba(240,234,214,.25)', marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

/* ── Main ────────────────────────────────────────────────────── */
export default function AdminFeedbackPage() {
  const [items,     setItems]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [expanded,  setExpanded]  = useState(null);
  const [filterCat, setFilterCat] = useState('all');
  const [filterSt,  setFilterSt]  = useState('all');
  const [sortBy,    setSortBy]    = useState('newest');
  const [noteEdit,  setNoteEdit]  = useState({});
  const [saving,    setSaving]    = useState({});
  const [search,    setSearch]    = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const PER_PAGE = 10;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api('/feedback?limit=500');
      setItems(data.items || []);
    } catch (err) {
      console.error('[AdminFeedbackPage]', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /* ── Derived counts ── */
  const newCount  = items.filter(i => i.status === 'new').length;
  const doneCount = items.filter(i => i.status === 'done').length;
  const catCounts = CATEGORIES.reduce((a, c) => ({ ...a, [c]: c === 'all' ? items.length : items.filter(i => i.category === c).length }), {});
  const stCounts  = STATUSES.reduce((a, s) => ({ ...a, [s]: s === 'all' ? items.length : items.filter(i => i.status === s).length }), {});

  /* ── Filter + sort ── */
  const visible = items
    .filter(i => filterCat === 'all' || i.category === filterCat)
    .filter(i => filterSt  === 'all' || i.status   === filterSt)
    .filter(i => {
      if (!search) return true;
      const q = search.toLowerCase();
      return i.message.toLowerCase().includes(q)
        || i.user?.name?.toLowerCase().includes(q)
        || i.name?.toLowerCase().includes(q)
        || i.user?.email?.toLowerCase().includes(q)
        || i.email?.toLowerCase().includes(q)
        || i.guestEmail?.toLowerCase().includes(q);
    })
    .sort((a, b) => {
      if (sortBy === 'oldest')   return new Date(a.createdAt) - new Date(b.createdAt);
      if (sortBy === 'category') return a.category.localeCompare(b.category);
      if (sortBy === 'status')   return a.status.localeCompare(b.status);
      return new Date(b.createdAt) - new Date(a.createdAt);
    });

  const totalPages = Math.max(1, Math.ceil(visible.length / PER_PAGE));
  const safePage   = Math.min(currentPage, totalPages);
  const paginated  = visible.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE);

  // Reset to page 1 whenever filters/search/sort change
  useEffect(() => { setCurrentPage(1); }, [filterCat, filterSt, sortBy, search]);

  /* ── Update ── */
  async function handleUpdate(id, patch) {
    setSaving(s => ({ ...s, [id]: true }));
    try {
      const data = await api(`/feedback/${id}`, { method: 'PUT', body: JSON.stringify(patch) });
      setItems(prev => prev.map(it => it.id === id ? data.feedback : it));
      if ('adminNote' in patch) {
        toastSuccess('Note saved successfully');
      } else if ('status' in patch) {
        const labels = { new: 'reset to New', reviewed: 'marked as Reviewed', done: 'marked as Done', dismissed: 'Dismissed' };
        toastInfo(`Feedback ${labels[patch.status] || 'updated'}`);
      }
    } catch (err) {
      console.error('[handleUpdate]', err);
    } finally {
      setSaving(s => ({ ...s, [id]: false }));
    }
  }

  return (
    <div className="fb-page">
      <style>{CSS}</style>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h1 style={{ margin: 0, fontFamily: "'Bebas Neue',sans-serif", fontSize: 28, letterSpacing: '.06em', color: 'var(--cream,#F0EAD6)', fontWeight: 400 }}>
              User Feedback
            </h1>
            {newCount > 0 && (
              <span style={{ background: '#C9A84C', color: '#050505', fontSize: 10, fontWeight: 800, padding: '3px 10px', borderRadius: 20, letterSpacing: '.06em' }}>
                {newCount} UNREAD
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: 'rgba(240,234,214,.35)', marginTop: 4 }}>
            Ibitekerezo by'abakoresha · {items.length} total submissions
          </div>
        </div>
        <button onClick={load} style={{ padding: '9px 18px', fontSize: 11, fontWeight: 700, letterSpacing: '.06em', cursor: 'pointer', background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.1)', color: 'rgba(240,234,214,.6)', borderRadius: 7, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 6, transition: 'all .15s', flexShrink: 0 }}
          onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(201,168,76,.35)'}
          onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,.1)'}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
          Refresh
        </button>
      </div>

      {/* ── Stat cards ── */}
      <div className="fb-stats">
        <StatCard label="Unread" value={newCount} sub="need attention" accent="#C9A84C" />
        <StatCard label="Total" value={items.length} sub="all time" />
        <StatCard label="Resolved" value={doneCount} sub="marked done" accent="#2ECC8A" />
        <StatCard label="This week" value={items.filter(i => Date.now() - new Date(i.createdAt) < 7*86400000).length} sub="last 7 days" />
      </div>

      {/* ── Search + sort ── */}
      <div className="fb-controls">
        <div className="fb-search" style={{ position: 'relative' }}>
          <svg style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', opacity: .35 }} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--cream,#F0EAD6)" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search messages, names, emails…"
            style={{ width: '100%', boxSizing: 'border-box', paddingLeft: 32, paddingRight: 12, paddingTop: 9, paddingBottom: 9, background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.1)', color: 'var(--cream,#F0EAD6)', fontSize: 12, borderRadius: 7, outline: 'none', fontFamily: 'Inter, sans-serif', transition: 'border-color .15s' }}
            onFocus={e => e.target.style.borderColor = 'rgba(201,168,76,.4)'}
            onBlur={e  => e.target.style.borderColor = 'rgba(255,255,255,.1)'}
          />
        </div>
        <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="fb-sort-select">
          <option value="newest">↓ Newest first</option>
          <option value="oldest">↑ Oldest first</option>
          <option value="category">Category</option>
          <option value="status">Status</option>
        </select>
      </div>

      {/* ── Category chips ── */}
      <div style={{ marginBottom: 10 }}>
        <div className="fb-section-label">Category</div>
        <div className="fb-chips">
          {CATEGORIES.map(c => (
            <button key={c} className={`fb-chip${filterCat === c ? ' active' : ''}`} onClick={() => setFilterCat(c)}>
              {c === 'all' ? `All (${catCounts.all})` : `${CAT_META[c].icon} ${CAT_META[c].label} (${catCounts[c]})`}
            </button>
          ))}
        </div>
      </div>

      {/* ── Status chips ── */}
      <div style={{ marginBottom: 20 }}>
        <div className="fb-section-label">Status</div>
        <div className="fb-chips">
          {STATUSES.map(s => (
            <button key={s} className={`fb-chip${filterSt === s ? ' active' : ''}`} onClick={() => setFilterSt(s)}>
              {s === 'all' ? `All (${stCounts.all})` : `${s} (${stCounts[s]})`}
            </button>
          ))}
        </div>
      </div>

      {/* ── Results label ── */}
      <div style={{ fontSize: 11, color: 'rgba(240,234,214,.25)', marginBottom: 12, letterSpacing: '.03em' }}>
        Showing {visible.length === 0 ? 0 : (safePage - 1) * PER_PAGE + 1}–{Math.min(safePage * PER_PAGE, visible.length)} of {visible.length} submissions
      </div>

      {/* ── Cards ── */}
      {loading ? (
        <div className="fb-empty">
          <div style={{ width: 28, height: 28, border: '2px solid rgba(201,168,76,.2)', borderTopColor: '#C9A84C', borderRadius: '50%', animation: 'spin .7s linear infinite', margin: '0 auto 12px' }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
          Loading feedback…
        </div>
      ) : visible.length === 0 ? (
        <div className="fb-empty">
          <div style={{ fontSize: 32, marginBottom: 10 }}>📭</div>
          {search ? 'No results match your search.' : 'No feedback submissions yet.'}
        </div>
      ) : paginated.map(item => {
        const isOpen   = expanded === item.id;
        const noteVal  = noteEdit[item.id] ?? item.adminNote ?? '';
        const cat      = CAT_META[item.category] || CAT_META.other;
        const st       = STATUS_META[item.status] || STATUS_META.new;
        const isNew    = item.status === 'new';

        // Registered users have item.user; guests have email/name stored directly on the item
        const displayName  = item.user?.name  || item.name  || 'Guest';
        const displayEmail = item.user?.email || item.email || item.guestEmail || '—';

        return (
          <div key={item.id} className={`fb-card${isOpen ? ' open' : ''}`}>

            {/* Row */}
            <div className="fb-card-row" onClick={() => setExpanded(isOpen ? null : item.id)}>
              {/* Category icon badge */}
              <div className="fb-cat-badge" style={{ background: `${cat.color}18`, border: `1px solid ${cat.color}30` }}>
                {cat.icon}
              </div>

              {/* Content */}
              <div className="fb-card-body">
                <div className="fb-card-msg" style={{ color: isNew ? 'var(--cream,#F0EAD6)' : 'rgba(240,234,214,.55)', fontWeight: isNew ? 600 : 400 }}>
                  {isNew && <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: '#C9A84C', marginRight: 7, verticalAlign: 'middle', boxShadow: '0 0 6px #C9A84C' }}/>}
                  {item.message}
                </div>
                <div className="fb-card-meta">
                  <span style={{ fontWeight: 600, color: 'rgba(240,234,214,.5)' }}>{displayName}</span>
                  <span>·</span>
                  <span>{displayEmail}</span>
                  <span>·</span>
                  <span>{fmtDate(item.createdAt)}</span>
                  {item.adminNote && <span style={{ color: 'rgba(201,168,76,.5)', fontStyle: 'italic' }}>· has note</span>}
                </div>
              </div>

              {/* Right */}
              <div className="fb-card-right">
                <span className="fb-status-pill" style={{ background: st.bg, border: `1px solid ${st.border}`, color: st.color }}>
                  {item.status}
                </span>
                <svg className={`fb-chevron${isOpen ? ' open' : ''}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
              </div>
            </div>

            {/* Expanded */}
            {isOpen && (
              <div className="fb-detail">
                {/* Full message */}
                <div className="fb-detail-msg">{item.message}</div>

                {/* Submitter info row */}
                <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
                  {[
                    { label: 'Submitted by', val: displayName },
                    { label: 'Email',        val: displayEmail },
                    { label: 'Category',     val: `${cat.icon} ${cat.label}` },
                    { label: 'Submitted',    val: fmtDateFull(item.createdAt) },
                  ].map(({ label, val }) => (
                    <div key={label} style={{ minWidth: 120 }}>
                      <div style={{ fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(240,234,214,.22)', marginBottom: 3 }}>{label}</div>
                      <div style={{ fontSize: 12, color: 'rgba(240,234,214,.7)' }}>{val}</div>
                    </div>
                  ))}
                </div>

                {/* Status update */}
                <div className="fb-section-label">Update status</div>
                <div className="fb-status-btns">
                  {['new', 'reviewed', 'done', 'dismissed'].map(s => {
                    const sm = STATUS_META[s];
                    const active = item.status === s;
                    return (
                      <button key={s} className="fb-status-btn"
                        onClick={() => handleUpdate(item.id, { status: s })}
                        disabled={saving[item.id]}
                        style={{
                          background: active ? sm.bg : 'rgba(255,255,255,.03)',
                          border: `1px solid ${active ? sm.border : 'rgba(255,255,255,.08)'}`,
                          color: active ? sm.color : 'rgba(240,234,214,.3)',
                          opacity: saving[item.id] ? .6 : 1,
                        }}>
                        <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: sm.dot, marginRight: 6, verticalAlign: 'middle' }}/>
                        {s}
                      </button>
                    );
                  })}
                </div>

                {/* Admin note */}
                <div className="fb-section-label" style={{ marginTop: 4 }}>Internal note <span style={{ color: 'rgba(240,234,214,.15)', fontStyle: 'italic', textTransform: 'none', letterSpacing: 0 }}>(only visible to admins)</span></div>
                <textarea
                  className="fb-note-input"
                  value={noteVal}
                  onChange={e => setNoteEdit(n => ({ ...n, [item.id]: e.target.value }))}
                  placeholder="Write a note about this submission…"
                  rows={2}
                />
                <button
                  className="fb-save-btn"
                  onClick={() => handleUpdate(item.id, { adminNote: noteVal })}
                  disabled={saving[item.id]}
                >
                  {saving[item.id] ? 'Saving…' : '✓ Save note'}
                </button>
              </div>
            )}
          </div>
        );
      })}

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginTop: 24, flexWrap: 'wrap' }}>
          {/* Prev */}
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={safePage === 1}
            style={{
              padding: '7px 14px', fontSize: 12, cursor: safePage === 1 ? 'not-allowed' : 'pointer',
              background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.1)',
              color: safePage === 1 ? 'rgba(240,234,214,.2)' : 'rgba(240,234,214,.6)',
              borderRadius: 6, transition: 'all .15s', display: 'flex', alignItems: 'center', gap: 5,
            }}
            onMouseEnter={e => { if (safePage !== 1) e.currentTarget.style.borderColor = 'rgba(201,168,76,.35)'; }}
            onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,.1)'}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
            Prev
          </button>

          {/* Page numbers */}
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(p => p === 1 || p === totalPages || Math.abs(p - safePage) <= 1)
            .reduce((acc, p, idx, arr) => {
              if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
              acc.push(p);
              return acc;
            }, [])
            .map((p, idx) =>
              p === '…' ? (
                <span key={`ellipsis-${idx}`} style={{ color: 'rgba(240,234,214,.2)', fontSize: 12, padding: '0 4px' }}>…</span>
              ) : (
                <button
                  key={p}
                  onClick={() => setCurrentPage(p)}
                  style={{
                    width: 34, height: 34, fontSize: 12, fontWeight: p === safePage ? 700 : 400,
                    cursor: p === safePage ? 'default' : 'pointer',
                    background: p === safePage ? 'rgba(201,168,76,.18)' : 'rgba(255,255,255,.04)',
                    border: `1px solid ${p === safePage ? 'rgba(201,168,76,.45)' : 'rgba(255,255,255,.1)'}`,
                    color: p === safePage ? '#C9A84C' : 'rgba(240,234,214,.5)',
                    borderRadius: 6, transition: 'all .15s',
                  }}
                  onMouseEnter={e => { if (p !== safePage) e.currentTarget.style.borderColor = 'rgba(201,168,76,.3)'; }}
                  onMouseLeave={e => { if (p !== safePage) e.currentTarget.style.borderColor = 'rgba(255,255,255,.1)'; }}
                >
                  {p}
                </button>
              )
            )
          }

          {/* Next */}
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={safePage === totalPages}
            style={{
              padding: '7px 14px', fontSize: 12, cursor: safePage === totalPages ? 'not-allowed' : 'pointer',
              background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.1)',
              color: safePage === totalPages ? 'rgba(240,234,214,.2)' : 'rgba(240,234,214,.6)',
              borderRadius: 6, transition: 'all .15s', display: 'flex', alignItems: 'center', gap: 5,
            }}
            onMouseEnter={e => { if (safePage !== totalPages) e.currentTarget.style.borderColor = 'rgba(201,168,76,.35)'; }}
            onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,.1)'}
          >
            Next
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
        </div>
      )}
    </div>
  );
}